<!-- PatientFlagBadges.vue -->
<script setup lang="ts">
    import { computed, reactive, watch, onBeforeMount, StyleValue } from 'vue';
    import { sortedAndVisiblePatientFlags, IPatientFlagPatientAssignItemWithDisplayOrder } from './PatientFlags/PatientFlagsUtils';
    import { IPatientFlagDisplayOrder, IPatientFlagPatientAssignItem } from '../../../lib/Models/Setup/PatientFlagSetupModel';
    import { DatabaseIdTypes, PortalType } from '../../../Enums/ChoiceEnums';
    import { usePatientFlagsStore } from '../../../lib/Stores/PatientFlagsStore'
    import { usePatientsStore } from '../../../lib/Stores/PatientsStore'     
    import XSpan from '../../HtmlWrapper/XSpan.vue';
    import PatientFlagBadge from './PatientFlagBadge.vue';
    import { timeout } from '../../../lib/ChoiceUtils';
    import { initPinia } from '../../../lib/PiniaMgr';
    import PatientActionButton from './PatientActionButton.vue';

    const patientFlagsStore = usePatientFlagsStore(initPinia());
    const patientsStore = usePatientsStore(initPinia());

    const props = withDefaults(defineProps<{
        patientEntityId?: number,
        displayContext?: PortalType,
        mouseOutDelayMilliseconds?: number, // How long after we stop hovering before we close the tool tip
        legacyPage?: boolean,               // Are we displaying on a legacy page (impacts how things are rendered)
        alignLeft?: boolean,                // Normally we align right, but this allows us to align left
        fontSize?: "smaller"|"default"
    }>(), {
        mouseOutDelayMilliseconds: 250,
        legacyPage: true
    });

    defineExpose({hidePatientFlagToolTip, loadData});

    const emit = defineEmits<{
        mouseOverFlag: []
    }>();
    
    const compData = reactive<{
        patientFlags?: IPatientFlagPatientAssignItemWithDisplayOrder[],
        patientFlagDisplayOrderValues?: IPatientFlagDisplayOrder[],
        flagToolTipStyle?: StyleValue,
        hoverFlag?: boolean,
        hoverFlagData?: IPatientFlagPatientAssignItem
    }>({});


    onBeforeMount(async ()=>{
        await loadData();
    });

    watch(() => props.patientEntityId, (_newVal, _oldVal) =>{
        loadData();
    });

    function fontSizeEm() {
        return props.fontSize == "smaller" ? "0.75rem" : "0.85rem";
    }

    async function loadData()
    {
        patientFlagsStore.getFlagDisplayOrder().then(
            resp => compData.patientFlagDisplayOrderValues = resp);            

        if (props.patientEntityId)
        {
            compData.patientFlags = await patientFlagsStore.getAssignedFlags(props.patientEntityId, DatabaseIdTypes.patient_entity_id);
        }
    }

    const assignedPatientFlags = computed<IPatientFlagPatientAssignItem[]|undefined>(() => 
    {
        if (!props.patientEntityId)
            return;

        return sortedAndVisiblePatientFlags(compData.patientFlags, compData.patientFlagDisplayOrderValues, props.displayContext);
    });

    function onMouseOverPatientFlag(evt: MouseEvent, flagData?: IPatientFlagPatientAssignItem)
    {
        emit("mouseOverFlag");

        if (flagData)
            compData.hoverFlagData = flagData;

        compData.hoverFlag = undefined !== compData.hoverFlagData;

        if (!flagData)
            return;

        const srcEle = (<HTMLElement>evt.target);
        const eleRect = srcEle.getBoundingClientRect();
        const flagSectionEle = document.querySelector(".flag-section");
        if (!flagSectionEle)
            return;

        const topInPx = eleRect.bottom + window.scrollY + 2;

        compData.flagToolTipStyle = {
            "display": "block",
            "position": "absolute",
            "font-size": fontSizeEm(),
            "z-index": 999,
            "left": `${eleRect.left}px`,
            "width": `${window.innerWidth - eleRect.left - 5}px`,
            "top": `${topInPx}px`,
            "max-height": `${window.innerHeight - topInPx - 5}px`,
            "border": "1px solid rgb(106, 140, 203)",
            "background-color": "rgb(241, 241, 241)",
            "margin-right": "0.5em",
            "margin-left": "0.25em",
            "overflow-y": "auto",
            "overflow-x": "hidden"
        }
    }
    function onMouseOutPatientFlag(evt: MouseEvent)
    {
        compData.hoverFlag = false;

        timeout(props.mouseOutDelayMilliseconds).then(() =>{
            if (!compData.hoverFlag)
            {
                hidePatientFlagToolTip();
            }
        });
    }

    function flagItemClass(flag: IPatientFlagPatientAssignItem)
    {
        if (flag !== compData.hoverFlagData)
            return;

        if (!props.legacyPage)
            return "highlight-flag-line";

        return "highlight-flag-line legacy-page-highlight-flag-line";
    }

    function hidePatientFlagToolTip()
    {
        compData.hoverFlag = false;
        compData.flagToolTipStyle = {
                    "display": "none" 
                };        
        compData.hoverFlagData = undefined;                
    }

    const flagSectionClass = computed<string>(() => {
        if (props.alignLeft)
            return "flag-section";
        return "flag-section flag-section-align-right";
    });

    const toolTipWidth = computed(() =>
    {
        return `width:{eleRect.left - 10}px;`;
    });
</script>

<style scoped>
    .flag-section {
        min-width: 2em;
    }

    .flag-section-align-right
    {
        text-align: right;
    }

    .patient-flag-tool-tip-line
    {
      height: 2em;
      padding-left: 0.2em;
      padding-right: 0.5em;
    }

    .highlight-flag-line {
      background-color:lightgrey; 
    }
    
    .legacy-page-highlight-flag-line
    {
        padding-top: 3px;
        margin-bottom:3px; 
        margin-top:3px;
    }

    .flag-tooltip-text {
        text-overflow: ellipsis; 
        white-space: nowrap; 
        display: inline-block; 
        overflow: hidden;
        width: 100%
    }
</style>

<template>
    <div :class="flagSectionClass">
        <XSpan v-for="(flag, index) in assignedPatientFlags"  @mouseover="evt => onMouseOverPatientFlag(evt, flag)" @mouseout="onMouseOutPatientFlag" :flagSetupId="flag.flagSetupId">
            <PatientFlagBadge :flag="flag" />
        </XSpan>
    </div>

    <Teleport to="body" v-if="compData.flagToolTipStyle" >
        <div :style="compData.flagToolTipStyle" @mouseover="evt => onMouseOverPatientFlag(evt)" @mouseout="onMouseOutPatientFlag">
            <div class="patient-flag-tool-tip-line isalus-header-primary-background" style="margin-bottom:0.1em">
                <b>Patient Flags</b>
            </div>

            <div v-for="flag in assignedPatientFlags" 
                    :class="flagItemClass(flag)"
                    class="patient-flag-tool-tip-line flag-tooltip-text">
                <PatientFlagBadge :flag="flag" :adjustForPadding="true"/> 

                <XSpan :title="flag.tooltip" style="vertical-align: middle; line-height:1.5em">
                    {{ flag.tooltip }}
                    <template v-if="flag.patientActionKeyGuid">
                        <PatientActionButton :patientActionKeyGuid="flag.patientActionKeyGuid" :databaseIdType="DatabaseIdTypes.patient_entity_id" :identifier="props.patientEntityId" />
                    </template>
                </XSpan>

            </div>
        </div>
    </Teleport>    
</template>